import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

/**
 * A curated set rather than a full emoji keyboard.
 *
 * Column icons need to read at a glance at small sizes, so this is deliberately
 * limited to shapes and faces that stay legible — and it keeps the picker
 * scannable instead of turning it into a search problem.
 */
const CHOICES = [
  '🟢', '🔴', '🟡', '🔵', '🟣', '⚫', '⚪', '🟠',
  '✅', '⚠️', '🛑', '➕', '➖', '⭐', '📌', '🔁',
  '😄', '😟', '😠', '😢', '😍', '🤔', '😅', '🥵',
  '💡', '🚀', '🔥', '🎯', '🎉', '🏆', '💪', '👏',
  '👍', '👎', '❤️', '🧩', '🗑️', '🩹', '🔭', '⚡',
  '🌱', '🌊', '🌤️', '🌧️', '🧊', '💨', '⚓', '🏝️',
  '🛠️', '📈', '📉', '📖', '🎓', '🗺️', '🕰️', '🪞',
  '🧱', '🪵', '🌾', '🍽️', '🤝', '🎁', '⏪', '⏩',
];

export function EmojiPicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (emoji: string) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={label}
        aria-expanded={open}
        className="flex h-10 w-10 items-center justify-center rounded-xl border border-black/10 bg-white text-xl transition hover:border-brand-500"
      >
        {value || <span className="text-sm text-ink/35">+</span>}
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-2xl bg-white p-2 shadow-panel">
          <div className="grid grid-cols-8 gap-0.5">
            {CHOICES.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onChange(emoji);
                  setOpen(false);
                }}
                className={clsx(
                  'rounded-md py-1 text-lg transition hover:bg-black/5',
                  value === emoji && 'bg-brand-100',
                )}
                aria-label={`Use ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>

          {value ? (
            <button
              type="button"
              className="mt-1 w-full rounded-lg py-1.5 text-xs font-medium text-ink/60 hover:bg-black/5"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Remove icon
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
